import { useModal } from '@/contexts/modal'
import useServicePriceTotal from '@/hooks/useServicePriceTotal'
import AddIcons from '@/icons/AddIcons'
import MergeIcons from '@/icons/MergeIcons'
import {
    PRICING_MODELS,
    PRODUCT_FORM,
    UNLIMITED,
} from '@/modules/Contract/constants'
import {
    Contract,
    ProductResponse,
    ServiceConfigOption,
} from '@/modules/Contract/types'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, GridHookForm, Typogaphy } from 'vnetwork-ui'
import ServiceTimes from '../../Base/ServiceTimes'
import Configuration from './Configuration'

interface CDNProps {
    isPOC?: boolean
    index: number
    product: ProductResponse
}

const CDN: React.FC<CDNProps> = ({ index, product, isPOC = false }) => {
    const formControl = useFormContext<Contract>()
    const { t } = useTranslation()
    const { setModal } = useModal()

    const key = `order.products.[${index}].service_configoptions`

    const service_configoptions = useWatch({
        control: formControl.control,
        name: key as Path<Contract>,
    }) as ServiceConfigOption[]

    // CDN bandwidth regions are typed 'bandwidth' on the main product and
    // 'base-config' on the addon product — both are mergeable regions here.
    const REGION_TYPES = ['bandwidth', 'base-config']
    const regionConfigs =
        service_configoptions?.filter((c) => REGION_TYPES.includes(c.type)) ||
        []
    const regionConfigCount = regionConfigs.length
    // Preserve the existing region type so merging an addon (base-config) doesn't
    // silently switch it to the bandwidth pricing model.
    const regionType = regionConfigs[0]?.type || 'bandwidth'

    // Handle merge bandwidth - combine all bandwidth configs into one global config
    const handleMergeBandwidth = () => {
        if (!service_configoptions || service_configoptions?.length === 0)
            return

        // Find all bandwidth region configs (bandwidth or base-config)
        const bandwidthConfigs = service_configoptions.filter((config) =>
            REGION_TYPES.includes(config.type)
        )

        if (bandwidthConfigs.length <= 1) return

        // Create a new global config, preserving the existing region type
        const globalConfig: ServiceConfigOption = {
            name: 'global',
            type: bandwidthConfigs[0]?.type || 'bandwidth',
            unit: bandwidthConfigs[0]?.unit || 'GB',
            prices: bandwidthConfigs[0]?.prices || [],
            price_config: bandwidthConfigs[0]?.price_config || {
                method: 'progressive',
            },
            commitment_prepaid: bandwidthConfigs[0]?.commitment_prepaid || true,
            statementable: bandwidthConfigs[0]?.statementable || true,
            commitment_value: bandwidthConfigs.reduce(
                (sum, config) => sum + (config.commitment_value || 0),
                0
            ),
            commitment_price_per_unit:
                bandwidthConfigs[0]?.commitment_price_per_unit,
            commitment_time: bandwidthConfigs[0]?.commitment_time,
        }

        // Keep non-region configs and add the new global config
        const otherConfigs = service_configoptions.filter(
            (config) => !REGION_TYPES.includes(config.type)
        )
        const newConfigs = [...otherConfigs, globalConfig]

        formControl.setValue(key as Path<Contract>, newConfigs)
    }

    // Handle add region - open modal to select region
    const handleAddRegion = () => {
        setModal('RegionSelectionModal', {
            onSelectRegion: (region: string) => {
                // Add new region config, matching the existing region type
                const newBandwidthConfig: Partial<ServiceConfigOption> = {
                    name: region,
                    type: regionType,
                    unit: 'GB',
                    prices: [
                        {
                            price_start: 0,
                            price_end: UNLIMITED,
                            price_per_unit: 0,
                        },
                    ],
                    price_config: {
                        method: 'progressive',
                    },
                    commitment_prepaid: true,
                    statementable: true,
                    commitment_value: 0,
                    commitment_price_per_unit: 0,
                    commitment_time: 1,
                }

                const currentConfigs = service_configoptions || []
                formControl.setValue(key as Path<Contract>, [
                    ...currentConfigs,
                    newBandwidthConfig,
                ])
            },
        })
    }

    useServicePriceTotal({
        control: formControl.control,
        index,
        service_configoptions,
        setValue: formControl.setValue,
        servicePriceModel: PRICING_MODELS.Bundle,
    })

    return (
        <div>
            <GridHookForm
                subForm
                actions={[]}
                data={[
                    {
                        formItemProps: {
                            name: `order.products.[${index}].product_form` as Path<Contract>,
                            type: 'select',
                            options: PRODUCT_FORM(t),
                            allowClear: false,
                            placeholder: t('service-form'),
                            label: t('service-form'),
                        },
                        className: isPOC
                            ? 'hidden'
                            : 'col-span-4 product-form-select',
                    },
                    {
                        renderEditComponent: () => {
                            return (
                                <div className="space-y-4">
                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-2 justify-between">
                                        <Typogaphy
                                            size="medium"
                                            className="font-medium"
                                        >
                                            {t('configuration-information')}
                                        </Typogaphy>
                                        <div className="flex gap-3 justify-end">
                                            <Button
                                                onClick={handleMergeBandwidth}
                                                startIcon={<MergeIcons />}
                                                variant="outlined"
                                                type="button"
                                                color="primary"
                                                data-tour="cdn-merge"
                                                disabled={
                                                    regionConfigCount <= 1
                                                }
                                            >
                                                {t('contract.merge_bandwidth')}
                                            </Button>

                                            <Button
                                                onClick={handleAddRegion}
                                                startIcon={<AddIcons />}
                                                variant="contained"
                                                color="primary"
                                                type="button"
                                                data-tour="cdn-add-region"
                                            >
                                                {t('contract.add_region')}
                                            </Button>
                                        </div>
                                    </div>
                                    {/* Configuration Component */}
                                    <div data-tour="cdn-config">
                                        <Configuration
                                            isPOC={isPOC}
                                            product_config_options={
                                                product.product_config_options
                                            }
                                            index={index}
                                        />
                                    </div>
                                </div>
                            )
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: `order.products.[${index}].service_price_register` as Path<Contract>,
                            type: 'number',
                            placeholder: t('service_price_register'),
                            label: t('service_price_register'),
                        },
                        className: isPOC ? 'hidden' : 'col-span-4',
                    },
                ]}
                formControl={formControl}
            />

            {!isPOC && <ServiceTimes index={index} />}
        </div>
    )
}

export default CDN
